import { ButtonLink } from './button'

/**
 * `NotFoundState` - the shared "nothing here" panel.
 *
 * `doc/design-system.md` lists this as a primitive, but it had been implemented
 * twice: once inline in `detail-page.tsx` for a missing record, once as
 * `pages/not-found.tsx` for an unrouted URL. This is the single copy; both
 * now compose it.
 *
 * `role="status"` rather than `alert`: a 404 is an outcome, not a failure
 * needing immediate interruption.
 *
 * Copy rule (the MUR "Design Tokens and Accessibility" page (accessibility bar)): the message never echoes what the user
 * searched for. `heading` and `description` are supplied by the caller and
 * must not interpolate a record identifier.
 */
export interface NotFoundStateProps {
  readonly heading: string
  readonly description: string
  readonly backTo?: string
  readonly backLabel?: string
}

export function NotFoundState({
  heading,
  description,
  backTo = '/members',
  backLabel = 'Back to members',
}: NotFoundStateProps): JSX.Element {
  return (
    <section
      role="status"
      aria-labelledby="not-found-heading"
      className="flex flex-col items-center gap-murmuration-3 px-murmuration-6 py-murmuration-7 text-center"
    >
      <h1 id="not-found-heading" tabIndex={-1} className="text-murmuration-h1 text-text-primary">
        {heading}
      </h1>
      <p className="max-w-[440px] text-murmuration-body text-text-secondary">{description}</p>
      <ButtonLink to={backTo} variant="primary" className="mt-murmuration-2">
        {backLabel}
      </ButtonLink>
    </section>
  )
}
